import { useCallback, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { InputParamSE, InputParamSEInstruction, DynamicDateOperation } from "../../../types/grid.ts";
import type { AdvancedFilterProps } from "./types.ts";
import styles from "./datePresetSelector.module.scss";

/**
 * Mode labels match the legacy WebAdmin2 scheduled-export form:
 *   "Basic"  — user enters a fixed date via the underlying DateFilter
 *              (or leaves the column unset; the grid's current value passes through).
 *   "Preset" — relative date built from Instructions (e.g. "Previous Day").
 *   "Ignore" — drop the filter entirely (only offered for non-parameter columns;
 *              mandatory function parameters can't be ignored).
 * Do not invent new labels — server-side and other forms use these exact terms.
 */
type Mode = "basic" | "preset" | "ignore";

interface DatePreset {
  label: string;
  value: string;
  build: (colName: string, isSimple: boolean, isEnd: boolean) => InputParamSE[];
}

function makeRangePreset(
  label: string,
  value: string,
  fromOps: DynamicDateOperation[],
  toOps: DynamicDateOperation[],
): DatePreset {
  return {
    label,
    value,
    build: (colName, isSimple, isEnd) => {
      // Type:Specified (8) matches the WebAdmin2 output for Instructions-driven
      // filters — when Instructions are present the dynamic-date evaluator
      // computes the actual comparison value at run time, so the Type slot
      // just records "value supplied", not a range operator.
      if (isSimple) {
        const ops = isEnd ? toOps : fromOps;
        const ins: InputParamSEInstruction = { OutputType: "Date", Args: ops };
        return [{ sColumn: colName, Type: ParamType.Specified, Instructions: ins }];
      }
      const fromIns: InputParamSEInstruction = { OutputType: "Date", Args: fromOps };
      const toIns: InputParamSEInstruction = { OutputType: "Date", Args: toOps };
      return [
        { sColumn: colName + "0", Type: ParamType.Specified, Instructions: fromIns },
        { sColumn: colName + "1", Type: ParamType.Specified, Instructions: toIns },
      ];
    },
  };
}

const DATE_PRESETS: DatePreset[] = [
  makeRangePreset("Previous Day", "prevDay",
    [{ Operation: "Adjust", Block: "Day", Value: -1 }, { Operation: "RoundDown", Block: "Day" }],
    [{ Operation: "Adjust", Block: "Day", Value: -1 }, { Operation: "RoundUp", Block: "Day" }],
  ),
  makeRangePreset("Previous Week", "prevWeek",
    [{ Operation: "Adjust", Block: "Week", Value: -1 }, { Operation: "RoundDown", Block: "Week" }],
    [{ Operation: "Adjust", Block: "Week", Value: -1 }, { Operation: "RoundUp", Block: "Week" }],
  ),
  makeRangePreset("Previous Month", "prevMonth",
    [{ Operation: "Adjust", Block: "Month", Value: -1 }, { Operation: "RoundDown", Block: "Month" }],
    [{ Operation: "Adjust", Block: "Month", Value: -1 }, { Operation: "RoundUp", Block: "Month" }],
  ),
  makeRangePreset("Current Month", "curMonth",
    [{ Operation: "RoundDown", Block: "Month" }],
    [{ Operation: "RoundUp", Block: "Month" }],
  ),
  makeRangePreset("Previous Year", "prevYear",
    [{ Operation: "Adjust", Block: "Year", Value: -1 }, { Operation: "RoundDown", Block: "Year" }],
    [{ Operation: "Adjust", Block: "Year", Value: -1 }, { Operation: "RoundUp", Block: "Year" }],
  ),
];

interface DatePresetSelectorProps {
  info: AdvancedFilterProps["info"];
  isParameter: AdvancedFilterProps["isParameter"];
  value: AdvancedFilterProps["value"];
  onChange: (value: InputParamSE[]) => void;
  onSearch: AdvancedFilterProps["onSearch"];
  cache: AdvancedFilterProps["cache"];
  /** The underlying date filter component (DateFilter or DateFilterSimple). */
  FilterComponent: React.ComponentType<AdvancedFilterProps>;
  /** True for DateFilterSimple (single date), false for DateFilter (range). */
  isSimple?: boolean;
}

function detectMode(value: AdvancedFilterProps["value"], canIgnore: boolean): Mode {
  if (!value || value.length === 0) return canIgnore ? "ignore" : "basic";
  for (const p of value) {
    if ((p as InputParamSE).Instructions) return "preset";
  }
  return "basic";
}

function detectPreset(value: AdvancedFilterProps["value"]): string {
  const fallback = DATE_PRESETS[0]?.value ?? "prevDay";
  if (!value || value.length === 0) return fallback;
  for (const p of value) {
    const ins = (p as InputParamSE).Instructions;
    if (!ins) continue;
    const key = JSON.stringify(ins.Args);
    for (const preset of DATE_PRESETS) {
      const test = preset.build("_", false, false);
      if (test.length > 0 && JSON.stringify((test[0] as InputParamSE).Instructions?.Args) === key) return preset.value;
    }
  }
  return fallback;
}

export function DatePresetSelector({
  info, isParameter, value, onChange, onSearch, cache,
  FilterComponent, isSimple = false,
}: DatePresetSelectorProps) {
  // Function parameters are mandatory — never offer "Ignore". Plain filter
  // columns can be skipped entirely.
  const canIgnore = !isParameter;
  const [mode, setMode] = useState<Mode>(() => detectMode(value, canIgnore));
  const [preset, setPreset] = useState(() => detectPreset(value));
  const isEnd = (info.FilterSettings ?? "").toUpperCase().includes("ISENDOFREPORT");

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    if (newMode === "preset") {
      const p = DATE_PRESETS.find(d => d.value === preset) ?? DATE_PRESETS[0]!;
      onChange(p.build(info.ColumnName, isSimple, isEnd));
    } else if (newMode === "ignore") {
      onChange([]);
    } else if (newMode === "basic") {
      // Drop Instructions so the FilterComponent owns the value cleanly.
      const cleaned = value.map(v => {
        const { Instructions: _, ...rest } = v as InputParamSE;
        return rest;
      });
      onChange(cleaned);
    }
  }, [preset, info.ColumnName, isSimple, isEnd, value, onChange]);

  const handlePresetChange = useCallback((val: string) => {
    setPreset(val);
    const p = DATE_PRESETS.find(d => d.value === val) ?? DATE_PRESETS[0]!;
    onChange(p.build(info.ColumnName, isSimple, isEnd));
  }, [info.ColumnName, isSimple, isEnd, onChange]);

  const handleBasicChange = useCallback((v: InputParamSE[]) => {
    // Strip Instructions if any sneak through from the inner editor.
    const cleaned = v.map(p => {
      const { Instructions: _, ...rest } = p as InputParamSE;
      return rest;
    });
    onChange(cleaned);
  }, [onChange]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.modeRow}>
        <label className={styles.modeLabel}>
          <input type="radio" name={`mode-${info.ColumnName}`} checked={mode === "basic"} onChange={() => handleModeChange("basic")} />
          Basic
        </label>
        <label className={styles.modeLabel}>
          <input type="radio" name={`mode-${info.ColumnName}`} checked={mode === "preset"} onChange={() => handleModeChange("preset")} />
          Preset
        </label>
        {canIgnore && (
          <label className={styles.modeLabel}>
            <input type="radio" name={`mode-${info.ColumnName}`} checked={mode === "ignore"} onChange={() => handleModeChange("ignore")} />
            Ignore
          </label>
        )}
      </div>

      {mode === "preset" && (
        <select
          className={styles.presetSelect}
          value={preset}
          onChange={e => handlePresetChange(e.target.value)}
        >
          {DATE_PRESETS.map(p => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      )}

      {mode === "basic" && (
        <FilterComponent
          info={info}
          isParameter={isParameter}
          value={value.filter(v => !(v as InputParamSE).Instructions)}
          onChange={handleBasicChange}
          onSearch={onSearch}
          cache={cache}
        />
      )}
    </div>
  );
}
